import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Car,
  User,
  LogOut,
  Edit,
  AlertTriangle,
  CheckCircle2,
  Clock,
  MapPin,
  Phone,
  Mail,
  Truck,
  Home,
  Calendar,
  CheckCircle,
  XCircle,
  FileText,
  Plus,
  Menu,
  X,
  LayoutDashboard
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import api from '../../config/api';
import DirectionsButton from './DirectionsButton';

const VehicleDashboard = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [bookingRequests, setBookingRequests] = useState([]);
  const [acceptedBookings, setAcceptedBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingBookings, setLoadingBookings] = useState(true);
  const [loadingAcceptedBookings, setLoadingAcceptedBookings] = useState(true);
  const [error, setError] = useState('');
  const [activeSection, setActiveSection] = useState('overview');
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('vehicleToken');
    if (!token) {
      navigate('/vehicle/login');
      return;
    }

    const fetchUserData = async () => {
      try {
        const response = await api.get('/vehicle/auth/me', {
          headers: { 'x-auth-token': token }
        });
        setUser(response.data.data);

        // Fetch the user's vehicles
        const vehiclesResponse = await api.get('/vehicle/user/my-vehicles', {
          headers: { 'x-auth-token': token }
        });
        setVehicles(vehiclesResponse.data.data || []);
      } catch (err) {
        console.error('Error fetching user data:', err);
        setError('Failed to load user data. Please try again.');
        toast.error('Failed to load user data');

        // If token is invalid, redirect to login
        if (err.response?.status === 401) {
          localStorage.removeItem('vehicleToken');
          localStorage.removeItem('vehicleUser');
          navigate('/vehicle/login');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  // Function to fetch booking requests
  const fetchBookingRequests = async () => {
    const token = localStorage.getItem('vehicleToken');
    if (!token) return;

    setLoadingBookings(true);
    try {
      const response = await api.get('/vehicle/bookings/public-requests', {
        headers: { 'x-auth-token': token }
      });
      setBookingRequests(response.data.data || []);
    } catch (err) {
      console.error('Error fetching booking requests:', err);
      toast.error('Failed to load booking requests');
    } finally {
      setLoadingBookings(false);
    }
  };

  // Function to fetch accepted bookings
  const fetchAcceptedBookings = async () => {
    const token = localStorage.getItem('vehicleToken');
    if (!token) return;

    setLoadingAcceptedBookings(true);
    try {
      const response = await api.get('/vehicle/bookings/accepted', {
        headers: { 'x-auth-token': token }
      });
      setAcceptedBookings(response.data.data || []);
    } catch (err) {
      console.error('Error fetching accepted bookings:', err);
    } finally {
      setLoadingAcceptedBookings(false);
    }
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchBookingRequests();
    fetchAcceptedBookings();
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('vehicleToken');
    localStorage.removeItem('vehicleUser');
    navigate('/vehicle/login');
    toast.success('Logged out successfully');
  };

  const handleBookingResponse = async (bookingId, status) => {
    const token = localStorage.getItem('vehicleToken');
    if (!token) return;

    try {
      await api.put(`/vehicle/bookings/${bookingId}/respond`,
        { status },
        { headers: { 'x-auth-token': token } }
      );

      toast.success(`Request ${status} successfully`);

      // Refresh the data
      fetchBookingRequests();
      fetchAcceptedBookings();
    } catch (err) {
      console.error('Error responding to booking:', err);
      toast.error('Failed to respond to request');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="mr-1 h-3 w-3" />
            Active
          </span>
        );
      case 'inactive':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
            <XCircle className="mr-1 h-3 w-3" />
            Inactive
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <Clock className="mr-1 h-3 w-3" />
            Pending
          </span>
        );
    }
  };

  // Render the appropriate content based on the active section
  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="space-y-6"
          >
            <div className="bg-white rounded-lg shadow-sm p-6 border border-gray-200">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Dashboard Overview</h2>
                  <p className="text-gray-500 mt-1">Welcome back, {user?.name}</p>
                </div>
                <div className="p-3 bg-red-50 rounded-full">
                  <LayoutDashboard className="h-6 w-6 text-red-600" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-gray-500 font-medium">Total Vehicles</span>
                    <Car className="h-5 w-5 text-gray-400" />
                  </div>
                  <div className="text-2xl font-bold text-gray-900">{vehicles.length}</div>
                </div>

                <div className="bg-red-50 p-4 rounded-xl border border-red-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-red-600 font-medium">Pending Requests</span>
                    <AlertTriangle className="h-5 w-5 text-red-500" />
                  </div>
                  <div className="text-2xl font-bold text-red-700">{bookingRequests.length}</div>
                </div>

                <div className="bg-green-50 p-4 rounded-xl border border-green-100">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-green-600 font-medium">Active Bookings</span>
                    <CheckCircle2 className="h-5 w-5 text-green-500" />
                  </div>
                  <div className="text-2xl font-bold text-green-700">{acceptedBookings.length}</div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <button
                onClick={() => navigate('/vehicle/register-vehicle')}
                className="p-6 bg-white rounded-lg shadow-sm border border-gray-200 hover:border-red-200 hover:shadow-md transition-all group text-left"
              >
                <div className="h-10 w-10 bg-red-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-red-100 transition-colors">
                  <Plus className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">Register New Vehicle</h3>
                <p className="text-sm text-gray-500">Add a new vehicle to your fleet for emergency services</p>
              </button>

              <button
                onClick={() => navigate('/vehicle/book-vehicle')}
                className="p-6 bg-white rounded-lg shadow-sm border border-gray-200 hover:border-red-200 hover:shadow-md transition-all group text-left"
              >
                <div className="h-10 w-10 bg-blue-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-blue-100 transition-colors">
                  <Calendar className="h-6 w-6 text-blue-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">Check Bookings</h3>
                <p className="text-sm text-gray-500">View public booking availability and schedule</p>
              </button>

              <button
                onClick={() => setActiveSection('profile')}
                className="p-6 bg-white rounded-lg shadow-sm border border-gray-200 hover:border-red-200 hover:shadow-md transition-all group text-left"
              >
                <div className="h-10 w-10 bg-purple-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-purple-100 transition-colors">
                  <User className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="font-semibold text-gray-900 mb-1">Update Profile</h3>
                <p className="text-sm text-gray-500">Manage your contact information and settings</p>
              </button>
            </div>
          </motion.div>
        );

      case 'profile':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* User Profile */}
            <div className="bg-white shadow rounded-lg mb-6">
              <div className="px-4 py-5 sm:px-6 flex flex-col sm:flex-row justify-between items-start sm:items-center">
                <div className="mb-4 sm:mb-0">
                  <h2 className="text-lg font-medium text-gray-900">Profile Information</h2>
                  <p className="mt-1 text-sm text-gray-500">Personal details and contact information</p>
                </div>
                <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full sm:w-auto">
                  <button
                    onClick={() => navigate('/vehicle/profile/edit')}
                    className="inline-flex items-center justify-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <Edit className="mr-1.5 h-4 w-4" />
                    Edit Profile
                  </button>
                  <button
                    onClick={handleLogout}
                    className="inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <LogOut className="mr-1.5 h-4 w-4" />
                    Logout
                  </button>
                </div>
              </div>
              <div className="border-t border-gray-200 px-4 py-5 sm:p-6">
                <div className="flex flex-col sm:flex-row items-center sm:items-start">
                  <div className="flex-shrink-0 h-16 w-16 rounded-full overflow-hidden bg-gray-200 flex items-center justify-center mb-4 sm:mb-0">
                    {user?.profileImage ? (
                      <img src={user.profileImage} alt={user.name} className="h-full w-full object-cover" />
                    ) : (
                      <User className="h-8 w-8 text-gray-400" />
                    )}
                  </div>
                  <div className="sm:ml-5 flex-grow text-center sm:text-left">
                    <h3 className="text-lg font-medium text-gray-900">{user?.name}</h3>
                    <div className="mt-1 grid grid-cols-1 gap-2 sm:grid-cols-2">
                      <div className="flex flex-col sm:flex-row items-center sm:items-center text-sm text-gray-500">
                        <Mail className="mb-1 sm:mb-0 sm:mr-1.5 h-4 w-4 text-gray-400" />
                        <span className="break-all">{user?.email}</span>
                      </div>
                      <div className="flex flex-col sm:flex-row items-center sm:items-center text-sm text-gray-500">
                        <Phone className="mb-1 sm:mb-0 sm:mr-1.5 h-4 w-4 text-gray-400" />
                        {user?.contactNumber}
                      </div>
                      <div className="flex flex-col sm:flex-row items-center sm:items-start text-sm text-gray-500 sm:col-span-2">
                        <MapPin className="mb-1 sm:mb-0 sm:mr-1.5 h-4 w-4 text-gray-400 flex-shrink-0" />
                        <span className="text-center sm:text-left">
                          {user?.address ? `${user.address}, ` : ''}
                          {user?.city ? `${user.city}, ` : ''}
                          {user?.state || ''}
                          {user?.pincode ? ` - ${user.pincode}` : ''}
                        </span>
                      </div>
                    </div>
                    <div className="mt-2 flex justify-center sm:justify-start">
                      {user?.isVerified ? (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          <CheckCircle2 className="mr-1" size={12} /> Email Verified
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <AlertTriangle className="mr-1" size={12} /> Email Not Verified
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        );

      case 'vehicles':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Your Vehicles */}
            <div className="bg-white shadow rounded-lg mb-6">
              <div className="px-4 py-5 sm:px-6 flex flex-col sm:flex-row justify-between items-start sm:items-center">
                <div className="mb-4 sm:mb-0">
                  <h2 className="text-lg font-medium text-gray-900">Your Vehicles</h2>
                  <p className="mt-1 text-sm text-gray-500">Manage your registered vehicles</p>
                </div>
                <button
                  onClick={() => navigate('/vehicle/register-vehicle')}
                  className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                >
                  <Car className="mr-1.5 h-4 w-4" />
                  Register New Vehicle
                </button>
              </div>
              <div className="border-t border-gray-200">
                {vehicles.length > 0 ? (
                  <ul className="divide-y divide-gray-200">
                    {vehicles.map((vehicle) => (
                      <li key={vehicle._id} className="px-4 py-4 sm:px-6">
                        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between">
                          <div className="flex flex-col sm:flex-row items-center sm:items-start mb-4 sm:mb-0 w-full sm:w-auto">
                            <div className="flex-shrink-0 h-12 w-12 rounded-md bg-gray-100 flex items-center justify-center mb-2 sm:mb-0">
                              {vehicle.vehicleType === 'ambulance' ? (
                                <Truck className="h-6 w-6 text-red-500" />
                              ) : (
                                <Car className="h-6 w-6 text-gray-500" />
                              )}
                            </div>
                            <div className="sm:ml-4 text-center sm:text-left">
                              <div className="text-sm font-medium text-gray-900">
                                {vehicle.vehicleType ? (vehicle.vehicleType.charAt(0).toUpperCase() + vehicle.vehicleType.slice(1)) : 'Vehicle'} - {vehicle.licensePlate}
                              </div>
                              <div className="text-sm text-gray-500">
                                {vehicle.make || 'N/A'} {vehicle.model || ''} {vehicle.year ? `(${vehicle.year})` : ''}
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 w-full sm:w-auto">
                            <div className="w-full sm:w-auto flex justify-center sm:justify-start">
                              {getStatusBadge(vehicle.status)}
                            </div>
                            <button
                              onClick={() => navigate(`/vehicle/edit/${vehicle._id}`)}
                              className="w-full sm:w-auto sm:ml-4 inline-flex items-center justify-center px-2.5 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                              <Edit className="mr-1 h-3 w-3" />
                              Details
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <div className="px-4 py-8 sm:px-6 text-center">
                    <Car className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">No vehicles registered</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Get started by registering your first vehicle.
                    </p>
                    <div className="mt-6">
                      <button
                        onClick={() => navigate('/vehicle/register-vehicle')}
                        className="w-full sm:w-auto inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                      >
                        <Car className="mr-2 h-4 w-4" />
                        Register New Vehicle
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        );

      case 'emergency':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Emergency Requests */}
            <div className="bg-white shadow rounded-lg mb-6">
              <div className="px-4 py-5 sm:px-6">
                <h2 className="text-lg font-medium text-gray-900">Emergency Requests</h2>
                <p className="mt-1 text-sm text-gray-500">View and respond to emergency transportation requests</p>
              </div>
              {loadingBookings ? (
                <div className="border-t border-gray-200 px-4 py-8 sm:px-6 text-center">
                  <p>Loading booking requests...</p>
                </div>
              ) : bookingRequests.length === 0 ? (
                <div className="border-t border-gray-200 px-4 py-8 sm:px-6 text-center">
                  <AlertTriangle className="mx-auto h-12 w-12 text-yellow-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No active emergency requests</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    You will be notified when there are emergency transportation requests in your area.
                  </p>
                </div>
              ) : (
                <div className="border-t border-gray-200">
                  {bookingRequests.map((booking) => (
                    <div key={booking._id} className="border-b border-gray-200 p-4">
                      <div className="flex flex-col md:flex-row justify-between">
                        <div className="mb-4 md:mb-0">
                          <h3 className="text-lg font-medium">{booking.name}</h3>
                          <div className="mt-2 flex items-center text-sm text-gray-500">
                            <Mail className="mr-1.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                            <span>{booking.email}</span>
                          </div>
                          <div className="mt-2 flex items-center text-sm text-gray-500">
                            <Phone className="mr-1.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                            <span>{booking.contactNumber}</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-start md:items-end">
                          <div className="flex items-center">
                            <Calendar className="mr-1.5 h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-500">{booking.date} at {booking.time}</span>
                          </div>
                          <div className="mt-2 inline-flex items-center rounded-full bg-yellow-100 px-2.5 py-0.5 text-xs font-medium text-yellow-800">
                            <Clock className="mr-1 h-3 w-3" />
                            Pending
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Pickup Location</h4>
                          <div className="mt-1 flex items-center">
                            <MapPin className="mr-1.5 h-4 w-4 text-gray-400" />
                            <span className="text-sm">{booking.pickupLocation}</span>
                          </div>
                          <div className="mt-1 text-xs text-gray-500">
                            {booking.pickupCity}, {booking.pickupState}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Drop Location</h4>
                          <div className="mt-1 flex items-center">
                            <MapPin className="mr-1.5 h-4 w-4 text-gray-400" />
                            <span className="text-sm">{booking.dropLocation}</span>
                          </div>
                          <div className="mt-1 text-xs text-gray-500">
                            {booking.dropCity}, {booking.dropState}
                          </div>
                        </div>
                      </div>

                      {booking.additionalInfo && (
                        <div className="mt-4">
                          <h4 className="text-sm font-medium text-gray-500">Additional Information</h4>
                          <p className="mt-1 text-sm text-gray-700">{booking.additionalInfo}</p>
                        </div>
                      )}

                      <div className="mt-4 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                        <button
                          onClick={() => handleBookingResponse(booking._id, 'accepted')}
                          className="flex-1 sm:flex-none inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                        >
                          <CheckCircle className="mr-1.5 h-4 w-4" />
                          Accept Request
                        </button>
                        <button
                          onClick={() => handleBookingResponse(booking._id, 'rejected')}
                          className="flex-1 sm:flex-none inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                        >
                          <XCircle className="mr-1.5 h-4 w-4" />
                          Decline Request
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        );

      case 'bookings':
        return (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
          >
            {/* Accepted Bookings */}
            <div className="bg-white shadow rounded-lg mb-6">
              <div className="px-4 py-5 sm:px-6">
                <h2 className="text-lg font-medium text-gray-900">Accepted Bookings</h2>
                <p className="mt-1 text-sm text-gray-500">Manage your accepted transportation bookings</p>
              </div>
              {loadingAcceptedBookings ? (
                <div className="border-t border-gray-200 px-4 py-8 sm:px-6 text-center">
                  <p>Loading accepted bookings...</p>
                </div>
              ) : acceptedBookings.length === 0 ? (
                <div className="border-t border-gray-200 px-4 py-8 sm:px-6 text-center">
                  <CheckCircle2 className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No accepted bookings</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    When you accept a booking request, it will appear here.
                  </p>
                </div>
              ) : (
                <div className="border-t border-gray-200">
                  {acceptedBookings.map((booking) => (
                    <div key={booking._id} className="border-b border-gray-200 p-4">
                      <div className="flex flex-col md:flex-row justify-between">
                        <div className="mb-4 md:mb-0">
                          <h3 className="text-lg font-medium">{booking.name}</h3>
                          <div className="mt-2 flex items-center text-sm text-gray-500">
                            <Mail className="mr-1.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                            <span>{booking.email}</span>
                          </div>
                          <div className="mt-2 flex items-center text-sm text-gray-500">
                            <Phone className="mr-1.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                            <span>{booking.contactNumber}</span>
                          </div>
                        </div>
                        <div className="flex flex-col items-start md:items-end">
                          <div className="flex items-center">
                            <Calendar className="mr-1.5 h-4 w-4 text-gray-400" />
                            <span className="text-sm text-gray-500">{booking.date} at {booking.time}</span>
                          </div>
                          <div className="mt-2 inline-flex items-center rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-medium text-green-800">
                            <CheckCircle className="mr-1 h-3 w-3" />
                            Accepted
                          </div>
                        </div>
                      </div>

                      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Pickup Location</h4>
                          <div className="mt-1 flex items-center">
                            <MapPin className="mr-1.5 h-4 w-4 text-gray-400" />
                            <span className="text-sm">{booking.pickupLocation}</span>
                          </div>
                          <div className="mt-1 text-xs text-gray-500">
                            {booking.pickupCity}, {booking.pickupState}
                          </div>
                        </div>
                        <div>
                          <h4 className="text-sm font-medium text-gray-500">Drop Location</h4>
                          <div className="mt-1 flex items-center">
                            <MapPin className="mr-1.5 h-4 w-4 text-gray-400" />
                            <span className="text-sm">{booking.dropLocation}</span>
                          </div>
                          <div className="mt-1 text-xs text-gray-500">
                            {booking.dropCity}, {booking.dropState}
                          </div>
                        </div>
                      </div>

                      {booking.additionalInfo && (
                        <div className="mt-4">
                          <h4 className="text-sm font-medium text-gray-500">Additional Information</h4>
                          <p className="mt-1 text-sm text-gray-700">{booking.additionalInfo}</p>
                        </div>
                      )}

                      <div className="mt-4">
                        <button
                          onClick={() => {
                            toast.info('This feature will be available soon');
                          }}
                          className="inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                        >
                          <CheckCircle2 className="mr-1.5 h-4 w-4" />
                          Mark as Completed
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <Car className="h-8 w-8 text-red-600" />
              <h1 className="text-2xl font-bold text-gray-900">Vehicle Owner Dashboard</h1>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => navigate('/')}
                className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
              >
                <Home className="h-5 w-5" />
              </button>
              <button
                onClick={handleLogout}
                className="p-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <LogOut className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile Menu Button */}
      <motion.button
        onClick={() => setIsMobileOpen(true)}
        className="fixed top-4 left-4 z-30 p-3 bg-white/95 backdrop-blur-md rounded-2xl shadow-xl lg:hidden hover:bg-gray-50 transition-all duration-300"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        <Menu className="w-5 h-5 text-gray-600" />
      </motion.button>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileOpen && (
          <>
            {/* Mobile Overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsMobileOpen(false)}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden backdrop-blur-sm"
            />

            {/* Mobile Sidebar */}
            <motion.aside
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25 }}
              className="fixed top-0 left-0 h-full w-[320px] bg-white/95 backdrop-blur-md z-50 shadow-2xl lg:hidden rounded-r-3xl overflow-hidden flex flex-col"
            >
              {/* Mobile Header */}
              <div className="h-16 flex items-center justify-between px-6 bg-gradient-to-r from-white to-gray-50 border-b border-gray-200/50 flex-shrink-0">
                <div className="flex items-center space-x-3">
                  <Car className="h-6 w-6 text-red-600" />
                  <h2 className="text-lg font-bold text-gray-900">Vehicle Dashboard</h2>
                </div>
                <motion.button
                  onClick={() => setIsMobileOpen(false)}
                  className="p-2 rounded-xl bg-gradient-to-br from-gray-100 to-gray-200 hover:from-gray-200 hover:to-gray-300 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <X className="w-5 h-5 text-gray-600" />
                </motion.button>
              </div>

              {/* Mobile Navigation */}
              <nav className="flex-1 px-6 py-6 overflow-y-auto">
                <div className="space-y-3 pb-6">
                  {/* Dashboard Options */}
                  <div className="mb-8">
                    <h3 className="text-sm font-medium text-gray-900 mb-4">Dashboard Options</h3>
                    <div className="space-y-2">
                      <button
                        onClick={() => {
                          setActiveSection('overview');
                          setIsMobileOpen(false);
                        }}
                        className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeSection === 'overview'
                            ? 'bg-red-50 text-red-700 border border-red-200'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                      >
                        <LayoutDashboard className="mr-3 h-5 w-5" />
                        Overview
                      </button>

                      <button
                        onClick={() => {
                          setActiveSection('profile');
                          setIsMobileOpen(false);
                        }}
                        className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeSection === 'profile'
                            ? 'bg-red-50 text-red-700 border border-red-200'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                      >
                        <User className="mr-3 h-5 w-5" />
                        Profile
                      </button>

                      <button
                        onClick={() => {
                          setActiveSection('vehicles');
                          setIsMobileOpen(false);
                        }}
                        className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeSection === 'vehicles'
                            ? 'bg-red-50 text-red-700 border border-red-200'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                      >
                        <Car className="mr-3 h-5 w-5" />
                        My Vehicles
                      </button>

                      <button
                        onClick={() => {
                          setActiveSection('emergency');
                          setIsMobileOpen(false);
                        }}
                        className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeSection === 'emergency'
                            ? 'bg-red-50 text-red-700 border border-red-200'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                      >
                        <AlertTriangle className="mr-3 h-5 w-5" />
                        Emergency Requests
                        {bookingRequests.length > 0 && (
                          <span className="ml-auto bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                            {bookingRequests.length}
                          </span>
                        )}
                      </button>

                      <button
                        onClick={() => {
                          setActiveSection('bookings');
                          setIsMobileOpen(false);
                        }}
                        className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeSection === 'bookings'
                            ? 'bg-red-50 text-red-700 border border-red-200'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                          }`}
                      >
                        <CheckCircle2 className="mr-3 h-5 w-5" />
                        Accepted Bookings
                        {acceptedBookings.length > 0 && (
                          <span className="ml-auto bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                            {acceptedBookings.length}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="pt-6 border-t border-gray-200">
                    <div className="space-y-2">
                      <button
                        onClick={() => {
                          navigate('/vehicle/register-vehicle');
                          setIsMobileOpen(false);
                        }}
                        className="w-full flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-colors"
                      >
                        <Plus className="mr-3 h-5 w-5" />
                        Register Vehicle
                      </button>

                      <button
                        onClick={() => {
                          navigate('/');
                          setIsMobileOpen(false);
                        }}
                        className="w-full flex items-center px-4 py-3 text-sm text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-colors"
                      >
                        <Home className="mr-3 h-5 w-5" />
                        Back to Home
                      </button>
                    </div>
                  </div>
                </div>
              </nav>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left Sidebar - Desktop Only */}
          {/* Left Sidebar - Desktop Only */}
          <aside className="hidden lg:block lg:w-64 bg-white rounded-lg shadow-sm border border-gray-200 p-4 h-fit">
            <nav className="space-y-1">
              <button
                onClick={() => setActiveSection('overview')}
                className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeSection === 'overview'
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
              >
                <LayoutDashboard className="mr-3 h-5 w-5" />
                Overview
              </button>

              <button
                onClick={() => setActiveSection('profile')}
                className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeSection === 'profile'
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
              >
                <User className="mr-3 h-5 w-5" />
                Profile
              </button>

              <button
                onClick={() => setActiveSection('vehicles')}
                className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeSection === 'vehicles'
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
              >
                <Car className="mr-3 h-5 w-5" />
                My Vehicles
              </button>

              <button
                onClick={() => setActiveSection('emergency')}
                className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeSection === 'emergency'
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
              >
                <AlertTriangle className="mr-3 h-5 w-5" />
                Emergency Requests
                {bookingRequests.length > 0 && (
                  <span className="ml-auto bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                    {bookingRequests.length}
                  </span>
                )}
              </button>

              <button
                onClick={() => setActiveSection('bookings')}
                className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${activeSection === 'bookings'
                  ? 'bg-red-50 text-red-700 border border-red-200'
                  : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'
                  }`}
              >
                <CheckCircle2 className="mr-3 h-5 w-5" />
                Accepted Bookings
                {acceptedBookings.length > 0 && (
                  <span className="ml-auto bg-green-100 text-green-800 text-xs font-medium px-2 py-1 rounded-full">
                    {acceptedBookings.length}
                  </span>
                )}
              </button>

              <div className="pt-4 mt-4 border-t border-gray-200">
                <button
                  onClick={() => navigate('/vehicle/register-vehicle')}
                  className="w-full flex items-center px-4 py-3 text-sm font-medium text-gray-700 hover:bg-gray-50 hover:text-gray-900 rounded-lg transition-colors"
                >
                  <Plus className="mr-3 h-5 w-5" />
                  Register Vehicle
                </button>
              </div>
            </nav>
          </aside>

          {/* Main Content Area */}
          <main className="flex-1">
            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                {error}
              </div>
            )}

            <AnimatePresence mode="wait">
              {renderContent()}
            </AnimatePresence>
          </main>
        </div>
      </div>
    </div>
  );
};

export default VehicleDashboard; 